import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IAuthState } from './types';
import { DiaryRootState } from 'app/appRoot/rootReducer';

import AdminService from 'external-services/diary-admin-service/admin.service';
import ManagerService from 'external-services/manager-service/manager.service';
import { IAccount, IDiaryVenue } from 'shared-types/DiaryTypes';
import { accountActions } from 'app/reducers/account/slice';
import { cloneDeep } from 'lodash-es';
import store from 'app/appRoot/Store';
import { errorStateActions, getAccordionDetail } from 'app/reducers/errorState/slice';
import { IAllAccountsResponse } from 'external-services/diary-admin-service/types';
import LoginActions from 'app/actions/login.actions';
const NS = 'authSlice';

const loadAccountAndVenue = async (): Promise<{
  account: IAccount;
  venue?: IDiaryVenue;
  isAdminUser?: boolean;
  hasAccountCustomerAccess?: boolean;
  allAccounts?: IAccount[];
  quantity?: number;
}> => {


  let allAccountsInfo: IAllAccountsResponse;
  let isAdminUser = false;
  let hasAccountCustomerAccess = false;
  let updatedAccount = {} as IAccount;
  try {

    /**
     * The login.controller.js is already setting the account, with "venues" array on it.
     * No need to request it again, just get it from cache.
     * Once AngularJS is completely removed from Diary, accountFromLegacyService will always be empty
     */
    const accountFromLegacyService = AdminService.getAccount();
    if (accountFromLegacyService) {
      updatedAccount = accountFromLegacyService;
    } else {
      // this conditon is probably never going to get called as AdminService.getAccount will return the account details
      const account = await AdminService.loadAccount();
      updatedAccount = await AdminService.getVenuesInfo(cloneDeep(account), account.accountId);
    }

    if (updatedAccount) {
      const managerState = ManagerService.setValuesFromAccountData(updatedAccount.roles);
      hasAccountCustomerAccess = managerState.hasAccountCustomerAccess;
      isAdminUser = AdminService.checkForAdminRoles(updatedAccount.roles);

      if (isAdminUser) {
        allAccountsInfo = await AdminService.getAllAccounts(0);
      }
    }
  } catch (error) {
    // don't need error handling as Diary will go into offline mode
  }
  return {
    account: updatedAccount,
    isAdminUser,
    hasAccountCustomerAccess,
    allAccounts: allAccountsInfo?.data,
    quantity: allAccountsInfo?.quantity,
  };
};

function loginErrorHanding(error: any, msg: string) {
  const { authReducer } = store.getState() as DiaryRootState;
  const WAIT = 10;
  let secsRemaining = WAIT;

  const logoutNow = () => {
    store.dispatch(
      logout({ isLegacyLogin: authReducer.isLegacyLogin, skipReload: false, stripQS: true })
    );
  };

  const dispatchMessage = () => {
    store.dispatch(
      errorStateActions.setErrorState({
        errorTitle: 'Login error',
        errorMessage: `${msg} Redirecting you back to login screen in ${secsRemaining} seconds.`,
        accordionDetail: getAccordionDetail(error),
        extraButton: {
          text: 'Back&nbsp;to login&nbsp;screen',
          cb: logoutNow,
        },
      })
    );
  };

  // counts down
  const intv = setInterval(() => {
    secsRemaining--;
    dispatchMessage();
  }, 1000);

  dispatchMessage();

  setTimeout(() => {
    if (intv) {
      clearInterval(intv);
    }
    logoutNow();
  }, WAIT * 1000);
}

/* This is for the old legacy login. Not needed for OAuth2
export const attemptLogin = createAsyncThunk(
  'auth/attemptLogin',
  async (
    payload: ILoginCreds,
    { dispatch, getState }
  ): Promise<{
    bearerToken: string;
    errorMessage?: IErrorMessage;
  }> => {
    const { username, password } = payload;
    const { authReducer } = getState() as DiaryRootState;
    let result: ILoginResponseData;
    let errorMessage: string;
    try {
      result = await LegacyAuthService.login({
        username,
        password,
      });
    } catch (err: any) {
      errorMessage = err.message;
    }

    if (result?.access_token) {
      try {
        const { account, isAdminUser, allAccounts, quantity } = await loadAccountAndVenue();

        // await dispatch(venueActions.setVenueDetails(venue));
        await dispatch(accountActions.setAccount(account));
        await dispatch(accountActions.setAccountLocked(ManagerService.accountLocked));
        await dispatch(accountActions.setIsManager(ManagerService.isManager));
        await dispatch(accountActions.setIsIncludedAdminRole(ManagerService.isIncludedAdminRole));
        await dispatch(accountActions.setAllAccountsForUser(allAccounts));
        await dispatch(accountActions.setAccountsQuantity(quantity));
        await dispatch(accountActions.setIsAdminUser(isAdminUser));
      } catch (err) {
        console.warn(NS, `Token was probably invalid`, err);
        dispatch(logout({ isLegacyLogin: authReducer.isLegacyLogin, skipReload: true }));
        dispatch(
          errorStateActions.setErrorState({
            errorTitle: 'Login error',
            errorMessage: `Sorry, there was a problem logging you in. Please try again. \n\n${CONTACT_MSG}`,
            accordionDetail: getAccordionDetail(err),
          })
        );
      }

      return {
        bearerToken: LegacyAuthService.getBearerToken(),
      };
    }

    return {
      bearerToken: '',
      errorMessage: {
        title: 'Login error',
        message: `Sorry, there was a problem logging you in. Please try again. \n\n${CONTACT_MSG}`,
        details: [errorMessage],
      },
    };
  }
);
 */

export const getAccountDetails = createAsyncThunk(
  'auth/getAccountDetails',
  async (payload: any, { dispatch, getState }): Promise<void> => {
    const { authReducer } = getState() as DiaryRootState;
    try {
      const { account, isAdminUser, hasAccountCustomerAccess, allAccounts, quantity } = await loadAccountAndVenue();

      // await dispatch(venueActions.setVenueDetails(venue));
      await dispatch(accountActions.setAccount(account));
      await dispatch(accountActions.setAllAccountsForUser(allAccounts));
      await dispatch(accountActions.setAccountsQuantity(quantity));
      await dispatch(accountActions.setAccountLocked(ManagerService.accountLocked));
      await dispatch(accountActions.setIsManager(ManagerService.isManager));
      await dispatch(accountActions.setIsAdminUser(isAdminUser));
      await dispatch(accountActions.setIsIncludedAdminRole(ManagerService.isIncludedAdminRole));
      await dispatch(accountActions.setHasAccountCustomerAccess(hasAccountCustomerAccess));
    } catch (err) {
      console.warn(NS, `Token was probably invalid`, err);

      loginErrorHanding(err, 'Possible invalid token.');
    }
  }
);

/* This function was for legacy login and is not used for OAuth2
export const autoLoginOnAppLoad = createAsyncThunk(
  'auth/autoLoginOnAppLoad',
  async (payload: void, { dispatch, getState }): Promise<{ bearerToken: string }> => {
    const { authReducer } = getState() as DiaryRootState;

    // needs this to be set for 'getBearerToken' to work
    let hasToken = LegacyAuthService.checkAuth();

    if (hasToken) {
      try {
        const { account, venue, isAdminUser, allAccounts, quantity } = await loadAccountAndVenue();

        // await dispatch(venueActions.setVenueDetails(venue));
        await dispatch(accountActions.setAccount(account));
        await dispatch(accountActions.setAllAccountsForUser(allAccounts));
        await dispatch(accountActions.setAccountsQuantity(quantity));
        await dispatch(accountActions.setIsAdminUser(isAdminUser));

        console.log(NS, 'account', account);
        console.log(NS, 'venue==>s', venue);
        dispatch(accountActions.setIsIncludedAdminRole(ManagerService.isIncludedAdminRole));
      } catch (err) {
        hasToken = false;
        console.warn(NS, `Token was probably invalid`, err);

        loginErrorHanding(err, 'Possible invalid token.');
      }
    }

    return {
      bearerToken: hasToken ? LegacyAuthService.getBearerToken() : '',
    };
  }
);
 */

// Need to use this once Oauth2 logout is implemented for Diary
export const logout = createAsyncThunk(
  'auth/logout',
  async (
    payload: { isLegacyLogin: boolean; skipReload?: boolean; stripQS?: boolean },
    { dispatch, getState }
  ) => {
    // to trigger legacy logout, for OAuth2 might need to do something else
    LoginActions.onTriggerLogout();
  }
);

const initialState: IAuthState = {
  isLoggedIn: false,
  bearerToken: '',
  loginError: null,
  oAuthBearerToken: '',
  isLegacyLogin: false,
  isVenueFullyLoaded: false,
};

const authSlice = createSlice({
  name: 'auth', // used in action types
  initialState,
  reducers: {
    setOAuthBearerToken: (state: IAuthState, action: PayloadAction<string>) => {
      state.oAuthBearerToken = action.payload;
      state.isLoggedIn = true;
    },
    setIsVenueFullyLoaded: (state: IAuthState, action: PayloadAction<boolean>) => {
      state.isVenueFullyLoaded = action.payload;
    },
  }
});

// Extract the action creators object and the reducer
const { actions, reducer } = authSlice;
// Extract and export each action creator by name
export { actions as authActions };
// Export the reducer, either as a default or named export
export default reducer;
